import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, TextField, Button, Table, TableHead, TableRow, TableCell, TableBody, 
  TableContainer, Paper, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, FormControl, 
  InputLabel, Select, MenuItem, Switch, FormControlLabel, Snackbar, Alert, Chip
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, 
  Devices as DevicesIcon, LocationOn as LocationIcon
} from '@mui/icons-material';
import api from '../api/api';

const FacilityManagement = () => {
  const [facilities, setFacilities] = useState([]);
  const [devices, setDevices] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeviceDialog, setOpenDeviceDialog] = useState(false);
  const [editingFacility, setEditingFacility] = useState(null);
  const [editingDevice, setEditingDevice] = useState(null);
  const [selectedFacility, setSelectedFacility] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const facilityTypes = [
    'Cafeteria', 'Gym', 'Shuttle', 'Parking', 'Lounge', 
    'MeetingRoom', 'Library', 'Medical', 'Other'
  ];

  const deviceTypes = [
    'Turnstile', 'RFIDReader', 'QRReader', 'POS', 'Gate', 'Button', 'Other'
  ];

  const showSnackbar = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const fetchFacilities = useCallback(async () => {
    try {
      const response = await api.get('/facilities');
      setFacilities(response.data.facilities || []);
    } catch (error) {
      console.error('Error fetching facilities:', error);
      showSnackbar('Tesisler yüklenirken hata oluştu', 'error');
    }
  }, [showSnackbar]);

  useEffect(() => {
    fetchFacilities();
  }, [fetchFacilities]);

  const fetchDevices = async (facilityId) => {
    try {
      const response = await api.get(`/facilities/${facilityId}/devices`);
      setDevices(response.data.devices || []);
    } catch (error) {
      console.error('Error fetching devices:', error);
      showSnackbar('Cihazlar yüklenirken hata oluştu', 'error');
    }
  };

  const handleOpenFacilityDialog = (facility = null) => {
    setEditingFacility(facility || {
      FacilityCode: '',
      FacilityName: '',
      FacilityType: '',
      Description: '',
      Location: '',
      Capacity: '',
      RequiresQuotaControl: false,
      RequiresTimeControl: false,
      RequiresApproval: false
    });
    setOpenDialog(true);
  };

  const handleOpenDeviceDialog = (device = null) => {
    setEditingDevice(device || {
      DeviceCode: '',
      DeviceName: '',
      DeviceType: '',
      LocationDescription: '',
      IPAddress: '',
      MACAddress: ''
    });
    setOpenDeviceDialog(true);
  };

  const handleCloseDialogs = () => {
    setOpenDialog(false);
    setOpenDeviceDialog(false);
    setEditingFacility(null);
    setEditingDevice(null);
  };

  const handleFacilitySubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editingFacility.ID ? `/facilities/${editingFacility.ID}` : '/facilities';
      const method = editingFacility.ID ? 'put' : 'post';
      
      await api[method](url, editingFacility);
      showSnackbar(`Tesis ${editingFacility.ID ? 'güncellendi' : 'eklendi'}`, 'success');
      fetchFacilities();
      handleCloseDialogs();
    } catch (error) {
      console.error('Error saving facility:', error);
      showSnackbar('Tesis kaydedilirken hata oluştu', 'error');
    }
  };

  const handleDeviceSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/facilities/${selectedFacility.ID}/devices`, editingDevice);
      showSnackbar('Cihaz eklendi', 'success');
      fetchDevices(selectedFacility.ID);
      handleCloseDialogs();
    } catch (error) {
      console.error('Error saving device:', error);
      showSnackbar('Cihaz kaydedilirken hata oluştu', 'error');
    }
  };

  const handleDeleteFacility = async (facilityId) => {
    if (window.confirm('Bu tesisi devre dışı bırakmak istediğinize emin misiniz?')) {
      try {
        await api.delete(`/facilities/${facilityId}`);
        showSnackbar('Tesis devre dışı bırakıldı', 'success');
        fetchFacilities();
      } catch (error) {
        console.error('Error deleting facility:', error);
        showSnackbar('Tesis devre dışı bırakılırken hata oluştu', 'error');
      }
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const getFacilityTypeColor = (type) => {
    const colors = {
      'Cafeteria': 'primary',
      'Gym': 'success',
      'Shuttle': 'warning',
      'Parking': 'info',
      'Lounge': 'secondary'
    };
    return colors[type] || 'default';
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Tesis Yönetimi</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenFacilityDialog()}
        >
          Yeni Tesis Ekle
        </Button>
      </Box>

      <TableContainer component={Paper} sx={{ mb: 3 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Tesis Kodu</TableCell>
              <TableCell>Tesis Adı</TableCell>
              <TableCell>Tesis Türü</TableCell>
              <TableCell>Konum</TableCell>
              <TableCell>Kapasite</TableCell>
              <TableCell>Kota Kontrolü</TableCell>
              <TableCell>Zaman Kontrolü</TableCell>
              <TableCell>Cihazlar</TableCell>
              <TableCell>İşlemler</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {facilities.map((facility) => (
              <TableRow key={facility.ID}>
                <TableCell>{facility.FacilityCode}</TableCell>
                <TableCell>{facility.FacilityName}</TableCell>
                <TableCell>
                  <Chip 
                    label={facility.FacilityType} 
                    color={getFacilityTypeColor(facility.FacilityType)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <LocationIcon fontSize="small" sx={{ mr: 1 }} />
                    {facility.Location || '-'}
                  </Box>
                </TableCell>
                <TableCell>{facility.Capacity || '-'}</TableCell>
                <TableCell>
                  <Switch checked={facility.RequiresQuotaControl} disabled />
                </TableCell>
                <TableCell>
                  <Switch checked={facility.RequiresTimeControl} disabled />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <DevicesIcon fontSize="small" sx={{ mr: 1 }} />
                    {facility.DeviceCount || 0}
                    <IconButton 
                      size="small" 
                      onClick={() => {
                        setSelectedFacility(facility);
                        fetchDevices(facility.ID);
                      }}
                      sx={{ ml: 1 }}
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </TableCell>
                <TableCell>
                  <IconButton 
                    onClick={() => handleOpenFacilityDialog(facility)} 
                    color="primary"
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton 
                    onClick={() => handleDeleteFacility(facility.ID)} 
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Devices Table */}
      {selectedFacility && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              {selectedFacility.FacilityName} - Cihazlar
            </Typography>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDeviceDialog()}
            >
              Cihaz Ekle
            </Button>
          </Box>

          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Cihaz Kodu</TableCell>
                <TableCell>Cihaz Adı</TableCell>
                <TableCell>Cihaz Türü</TableCell>
                <TableCell>Konum</TableCell>
                <TableCell>IP Adresi</TableCell>
                <TableCell>MAC Adresi</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {devices.map((device) => (
                <TableRow key={device.ID}>
                  <TableCell>{device.DeviceCode}</TableCell>
                  <TableCell>{device.DeviceName}</TableCell>
                  <TableCell>
                    <Chip label={device.DeviceType} size="small" />
                  </TableCell>
                  <TableCell>{device.LocationDescription || '-'}</TableCell>
                  <TableCell>{device.IPAddress || '-'}</TableCell>
                  <TableCell>{device.MACAddress || '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* Facility Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialogs} maxWidth="md" fullWidth>
        <form onSubmit={handleFacilitySubmit}>
          <DialogTitle>
            {editingFacility?.ID ? 'Tesis Düzenle' : 'Yeni Tesis Ekle'}
          </DialogTitle>
          <DialogContent>
            <TextField
              margin="normal"
              fullWidth
              label="Tesis Kodu"
              name="FacilityCode"
              value={editingFacility?.FacilityCode || ''}
              onChange={(e) => setEditingFacility(prev => ({ ...prev, FacilityCode: e.target.value }))}
              required
              disabled={!!editingFacility?.ID}
            />
            <TextField
              margin="normal"
              fullWidth
              label="Tesis Adı"
              name="FacilityName"
              value={editingFacility?.FacilityName || ''}
              onChange={(e) => setEditingFacility(prev => ({ ...prev, FacilityName: e.target.value }))}
              required
            />
            <FormControl fullWidth margin="normal">
              <InputLabel>Tesis Türü</InputLabel>
              <Select
                value={editingFacility?.FacilityType || ''}
                onChange={(e) => setEditingFacility(prev => ({ ...prev, FacilityType: e.target.value }))}
                required
              >
                {facilityTypes.map(type => (
                  <MenuItem key={type} value={type}>{type}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              margin="normal"
              fullWidth
              multiline
              rows={2}
              label="Açıklama"
              name="Description"
              value={editingFacility?.Description || ''}
              onChange={(e) => setEditingFacility(prev => ({ ...prev, Description: e.target.value }))}
            />
            <TextField
              margin="normal"
              fullWidth
              label="Konum"
              name="Location"
              value={editingFacility?.Location || ''}
              onChange={(e) => setEditingFacility(prev => ({ ...prev, Location: e.target.value }))}
            />
            <TextField
              margin="normal"
              fullWidth
              type="number"
              label="Kapasite"
              name="Capacity"
              value={editingFacility?.Capacity || ''}
              onChange={(e) => setEditingFacility(prev => ({ ...prev, Capacity: e.target.value }))}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={editingFacility?.RequiresQuotaControl || false}
                  onChange={(e) => setEditingFacility(prev => ({ 
                    ...prev, 
                    RequiresQuotaControl: e.target.checked 
                  }))}
                />
              }
              label="Kota Kontrolü"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={editingFacility?.RequiresTimeControl || false}
                  onChange={(e) => setEditingFacility(prev => ({ 
                    ...prev, 
                    RequiresTimeControl: e.target.checked 
                  }))}
                />
              }
              label="Zaman Kontrolü"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={editingFacility?.RequiresApproval || false}
                  onChange={(e) => setEditingFacility(prev => ({ 
                    ...prev, 
                    RequiresApproval: e.target.checked 
                  }))}
                />
              }
              label="Onay Gerektirir"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialogs}>İptal</Button>
            <Button type="submit" variant="contained" color="primary">
              {editingFacility?.ID ? 'Güncelle' : 'Ekle'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Device Dialog */}
      <Dialog open={openDeviceDialog} onClose={handleCloseDialogs} maxWidth="sm" fullWidth>
        <form onSubmit={handleDeviceSubmit}>
          <DialogTitle>Cihaz Ekle</DialogTitle>
          <DialogContent>
            <TextField
              margin="normal"
              fullWidth
              label="Cihaz Kodu"
              name="DeviceCode"
              value={editingDevice?.DeviceCode || ''}
              onChange={(e) => setEditingDevice(prev => ({ ...prev, DeviceCode: e.target.value }))}
              required
            />
            <TextField
              margin="normal"
              fullWidth
              label="Cihaz Adı"
              name="DeviceName"
              value={editingDevice?.DeviceName || ''}
              onChange={(e) => setEditingDevice(prev => ({ ...prev, DeviceName: e.target.value }))}
              required
            />
            <FormControl fullWidth margin="normal">
              <InputLabel>Cihaz Türü</InputLabel>
              <Select
                value={editingDevice?.DeviceType || ''}
                onChange={(e) => setEditingDevice(prev => ({ ...prev, DeviceType: e.target.value }))}
                required
              >
                {deviceTypes.map(type => (
                  <MenuItem key={type} value={type}>{type}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              margin="normal"
              fullWidth
              label="Konum Açıklaması"
              name="LocationDescription"
              value={editingDevice?.LocationDescription || ''}
              onChange={(e) => setEditingDevice(prev => ({ ...prev, LocationDescription: e.target.value }))}
            />
            <TextField
              margin="normal"
              fullWidth
              label="IP Adresi"
              name="IPAddress"
              value={editingDevice?.IPAddress || ''}
              onChange={(e) => setEditingDevice(prev => ({ ...prev, IPAddress: e.target.value }))}
            />
            <TextField
              margin="normal"
              fullWidth
              label="MAC Adresi"
              name="MACAddress"
              value={editingDevice?.MACAddress || ''}
              onChange={(e) => setEditingDevice(prev => ({ ...prev, MACAddress: e.target.value }))}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialogs}>İptal</Button>
            <Button type="submit" variant="contained" color="primary">Ekle</Button>
          </DialogActions>
        </form>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FacilityManagement;
